import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

import { useWebmaster } from "context/admin/webmaster";
import useResponsive from "libs/helpers/useResponsive";
import usePagination from "libs/helpers/usePagination";

import DataTile from "components/DataTile";
import TaskInfo from "components/TaskInfo";
import PillGridNew from "components/PillGridNew";
import Pagination from "components/Pagination";
import Button from "components/Button";
import Select from "components/form/Select";
import ScrollTopMobile from "components/ScrollTopMobile";
import Loader from "components/Loader";
import ModalFilters from "./ModalFilters";

import { TASKS_PER_PAGE } from "libs/mobile_settings";
import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";
import { tasks_sorting } from "libs/sorting";
import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";

import { ReactComponent as NoDataIcon } from "assets/icons/no-data.svg";

const mainClass = "webmasters-admin-details";

const TasksTiles = ({ setWebsiteInfoId, setExplanation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { max } = useResponsive();
  const {
    data,
    loading,
    filters,
    filterHandler,
    filterClearAll,
    applyFilters,
    updateTask,
  } = useWebmaster();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const perPage = max.sm ? TASKS_PER_PAGE.mobile : TASKS_PER_PAGE.default;
  const { currentPage, totalPages, handlePageChange, paginatedData } =
    usePagination(data?.tasks, perPage);

  useEffect(() => {
    if (!max.sm && isMobileFiltersOpen) setIsMobileFiltersOpen(false);
  }, [max.sm, isMobileFiltersOpen]);

  useEffect(() => {
    if (!max.sm) return;

    const priorityFilters = filters?.priority?.split(",");
    const statusFilters = filters?.status?.split(",");
    const snapshot = {
      priority:
        priorityFilters?.length > 0
          ? task_priority_data?.find(
              (item) => item?.key === priorityFilters[0],
            ) || null
          : null,
      status:
        statusFilters?.length > 0
          ? task_status_data?.find((item) => item?.key === statusFilters[0]) ||
            null
          : null,
    };

    if (priorityFilters?.length > 1 || statusFilters?.length > 1)
      applyFilters(snapshot);
  }, [filters, applyFilters, max.sm]);

  const statusOptions = task_status_data
    .filter(({ key }) =>
      !["client_verification", "closed", "canceled"].includes(key),
    )
    .map((item) => ({ ...item, label: t(item.label) }));

  return (
    <>
      <div className={`${mainClass}__tiles`}>
        <div className={`${mainClass}__tiles-header`}>
          <div className={`${mainClass}__tiles-header-filters`}>
            {max.sm ? (
              <>
                <div>
                  <Button
                    variant="transparent"
                    onClick={() => setIsMobileFiltersOpen(true)}
                  >
                    {t("Filter results")}
                  </Button>
                </div>
                <div>
                  <Button
                    disabled={
                      Object.keys(filters).filter(
                        (item) => !["sort", "page"].includes(item),
                      ).length === 0
                    }
                    onClick={filterClearAll}
                  >
                    {t("Clear filters")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <PillGridNew
                    header="Status"
                    label={t("Select status")}
                    selected={filters?.status}
                    options={statusOptions}
                    optionsFooter={
                      <TaskInfo onClick={() => setExplanation("status")} />
                    }
                    onChange={(option) =>
                      filterHandler("status", option.key, true)
                    }
                  />
                </div>
                <div>
                  <PillGridNew
                    header={t("Priority")}
                    label={t("Select priority")}
                    selected={filters?.priority}
                    icon="flag"
                    options={task_priority_data.map((item) => ({
                      ...item,
                      label: t(item.label),
                    }))}
                    optionsFooter={
                      <TaskInfo
                        type="priority"
                        onClick={() => setExplanation("priority")}
                      />
                    }
                    onChange={(option) =>
                      filterHandler("priority", option.key, true)
                    }
                  />
                </div>
              </>
            )}
          </div>
          <div className={`${mainClass}__tiles-header-sort`}>
            <Select.Sort
              initial={filters?.sort}
              options={tasks_sorting}
              forceBottom
              onChange={(option) => filterHandler("sort", option.value)}
            />
          </div>
        </div>
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={currentPage}
            className={`${mainClass}__tiles-row`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "tween", duration: 0.3 }}
          >
            {!loading ? (
              paginatedData.length > 0 ? (
                paginatedData.map(
                  ({
                    _id,
                    created_at,
                    priority,
                    title,
                    website,
                    website_id,
                    total_time,
                    status,
                  }) => {
                    const statusData = task_status_data?.find(
                      (item) => item?.value === status,
                    );
                    const priorityData = task_priority_data?.find(
                      (item) => item?.value === priority,
                    );
                    const isCompletelyDisabled =
                      statusData?.key === "canceled";

                    return (
                      <DataTile
                        key={_id}
                        label={title}
                        status={{
                          data: {
                            value: statusData?.value,
                            label: t(statusData?.label),
                            color: statusData?.color,
                          },
                          options: !isCompletelyDisabled
                            ? task_status_data.map((item) => ({
                                ...item,
                                label: t(item.label),
                              }))
                            : [],
                          optionsFooter: !isCompletelyDisabled ? (
                            <TaskInfo
                              onClick={() => setExplanation("status")}
                            />
                          ) : null,
                          isCompletelyDisabled,
                          isCompact: true,
                          onChange: !isCompletelyDisabled
                            ? (option) =>
                                updateTask(_id, { status: option.value })
                            : undefined,
                        }}
                        priority={{
                          data: {
                            value: priorityData?.value,
                            label: t(priorityData?.label),
                            color: priorityData?.color,
                          },
                          icon: "flag",
                          options: !isCompletelyDisabled
                            ? task_priority_data.map((item) => ({
                                ...item,
                                label: t(item.label),
                              }))
                            : [],
                          optionsFooter: !isCompletelyDisabled ? (
                            <TaskInfo
                              type="priority"
                              onClick={() => setExplanation("priority")}
                            />
                          ) : null,
                          isCompletelyDisabled,
                          isCompact: true,
                          onChange: !isCompletelyDisabled
                            ? (option) =>
                                updateTask(_id, { priority: option.value })
                            : undefined,
                        }}
                        time_tracker={{
                          readOnly: total_time?.$numberDecimal
                            ? toHoursAndMinutes(total_time.$numberDecimal)
                                ?.formatted
                            : "-",
                        }}
                        created_at={created_at}
                        website={{
                          name: website,
                          onClick: () => setWebsiteInfoId(website_id),
                        }}
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (filters?.status)
                            params.set("status", filters.status);
                          if (filters?.priority)
                            params.set("priority", filters.priority);
                          if (filters?.sort) params.set("sort", filters.sort);
                          if (currentPage > 1) params.set("page", currentPage);
                          params.set(
                            "returnTo",
                            `${location.pathname}${location.search}`,
                          );
                          const query = params.toString();
                          navigate(`/tasks/${_id}${query ? `?${query}` : ""}`);
                        }}
                      />
                    );
                  },
                )
              ) : (
                <div className={`${mainClass}__tiles-no-data`}>
                  <NoDataIcon />
                  <p>{t("No data")}</p>
                </div>
              )
            ) : (
              <div className={`${mainClass}__tiles-loader`}>
                <Loader color="blue" center size="large" />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        {totalPages > 1 && (
          <div className={`${mainClass}__tiles-pagination`}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={handlePageChange}
            />
          </div>
        )}
        <div className={`${mainClass}__tiles-scroll`}>
          <ScrollTopMobile />
        </div>
      </div>
      {max.sm && (
        <ModalFilters
          isOpen={isMobileFiltersOpen}
          onClose={() => setIsMobileFiltersOpen(false)}
        />
      )}
    </>
  );
};

export default TasksTiles;